import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { profileService } from "@/services/profileService";
import { SPECIALTIES, CONTACT_METHODS, US_STATES } from "@/config/formOptions";
import { FormSection, Field, ChipMultiSelect, StatePicker } from "@/components/profile/FormControls";
import { LoadingState } from "@/components/common/StateBlocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  first_name: "", last_name: "", display_name: "", business_name: "", brokerage_name: "",
  primary_market: "", primary_licensed_state: "", additional_licensed_states: [],
  years_in_real_estate: "", specialties: [], website: "", bio: "", preferred_contact_method: "",
};

const fieldErrors = (err) => {
  const list =
    err?.details?.errors ||
    err?.error?.details?.errors ||
    err?.response?.data?.error?.details?.errors ||
    [];
  const map = {};
  list.forEach((e) => {
    const f = e.loc?.[e.loc.length - 1];
    if (f) map[f] = (e.msg || "").replace("Value error, ", "");
  });
  return map;
};

export const BusinessProfileForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(null);
  const [active, setActive] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const [p, c] = await Promise.all([profileService.getBusiness(), profileService.getCompleteness()]);
      if (p) {
        const clean = Object.fromEntries(
          Object.entries(p).map(([k, v]) => [k, v === null ? (Array.isArray(EMPTY[k]) ? [] : "") : v]),
        );
        setForm({ ...EMPTY, ...clean, years_in_real_estate: p.years_in_real_estate ?? "" });
      }
      setPercent(c.business.percent);
      setActive({ is_active: c.business.is_active, missing_for_active: c.business.missing_for_active || [] });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        preferred_contact_method: form.preferred_contact_method || null,
        primary_licensed_state: form.primary_licensed_state || null,
        years_in_real_estate: form.years_in_real_estate === "" ? null : Number(form.years_in_real_estate),
      };
      await profileService.saveBusiness(payload);
      const c = await profileService.getCompleteness();
      setPercent(c.business.percent);
      setActive({ is_active: c.business.is_active, missing_for_active: c.business.missing_for_active || [] });
      toast.success("Business profile saved");
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-10"><LoadingState label="Loading your profile" /></div>;

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="business-profile-form" noValidate>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Your professional identity and business context.</p>
        <div className="flex items-center gap-2">
          {active && (
            active.is_active ? (
              <Badge className="gap-1 bg-emerald-600 text-white hover:bg-emerald-600" data-testid="business-active-badge">
                <ShieldCheck className="h-3.5 w-3.5" /> Active
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 border-amber-400/60 text-amber-600" data-testid="business-inactive-badge">
                <AlertCircle className="h-3.5 w-3.5" /> Not yet active
              </Badge>
            )
          )}
          {percent != null && (
            <Badge variant="outline" className="border-accent/40 text-accent" data-testid="business-completeness">
              {percent}% complete
            </Badge>
          )}
        </div>
      </div>

      {active && !active.is_active && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="business-active-hint">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Add your <strong>primary market</strong> and <strong>primary licensed state</strong> to fully activate this profile for business-intelligence and compliance features.</span>
        </div>
      )}

      <FormSection title="Professional Identity">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" htmlFor="first_name" error={errors.first_name}>
            <Input id="first_name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="bp-first-name" />
          </Field>
          <Field label="Last name" htmlFor="last_name" error={errors.last_name}>
            <Input id="last_name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="bp-last-name" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Professional / display name" htmlFor="display_name" error={errors.display_name}>
            <Input id="display_name" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="bp-display-name" />
          </Field>
          <Field label="Preferred contact method" htmlFor="preferred_contact_method" error={errors.preferred_contact_method}>
            <Select value={form.preferred_contact_method || ""} onValueChange={(v) => set("preferred_contact_method", v)}>
              <SelectTrigger id="preferred_contact_method" data-testid="bp-contact-method"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {CONTACT_METHODS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </FormSection>

      <FormSection title="Business">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Business name (optional)" htmlFor="business_name" error={errors.business_name}>
            <Input id="business_name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="bp-business-name" />
          </Field>
          <Field label="Brokerage name (optional)" htmlFor="brokerage_name" error={errors.brokerage_name}>
            <Input id="brokerage_name" value={form.brokerage_name} onChange={(e) => set("brokerage_name", e.target.value)} data-testid="bp-brokerage" />
          </Field>
        </div>
        <Field label="Website (optional)" htmlFor="website" error={errors.website} hint="e.g. yoursite.com">
          <Input id="website" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="bp-website" />
        </Field>
      </FormSection>

      <FormSection title="Market & Specialties">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Primary market" htmlFor="primary_market" error={errors.primary_market}>
            <Input id="primary_market" value={form.primary_market} onChange={(e) => set("primary_market", e.target.value)} data-testid="bp-primary-market" placeholder="City, State" />
          </Field>
          <Field label="Primary licensed state" htmlFor="primary_licensed_state" error={errors.primary_licensed_state}>
            <Select value={form.primary_licensed_state || ""} onValueChange={(v) => set("primary_licensed_state", v)}>
              <SelectTrigger id="primary_licensed_state" data-testid="bp-primary-state"><SelectValue placeholder="Select state…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Additional licensed states" error={errors.additional_licensed_states} hint="For business context only — detailed licensing arrives in the Compliance phase.">
          <StatePicker options={US_STATES} value={form.additional_licensed_states} onChange={(v) => set("additional_licensed_states", v)} testid="bp-additional-states" />
        </Field>
        <Field label="Specialties" error={errors.specialties}>
          <ChipMultiSelect options={SPECIALTIES} value={form.specialties} onChange={(v) => set("specialties", v)} testid="bp-specialties" />
        </Field>
      </FormSection>

      <FormSection title="Professional Details">
        <Field label="Years in real estate (optional)" htmlFor="years" error={errors.years_in_real_estate}>
          <Input id="years" type="number" min="0" max="80" value={form.years_in_real_estate} onChange={(e) => set("years_in_real_estate", e.target.value)} data-testid="bp-years" className="w-40" />
        </Field>
        <Field label="Professional bio (optional)" htmlFor="bio" error={errors.bio}>
          <Textarea id="bio" rows={5} value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="bp-bio" placeholder="Tell clients about your experience and approach…" />
        </Field>
      </FormSection>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving} data-testid="bp-save">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="h-4 w-4" /> Save profile</>}
        </Button>
      </div>
    </form>
  );
};
